import {
  ColDef,
  IRichCellEditorParams,
  SuppressKeyboardEventParams,
} from '@ag-grid-community/core';
import { equipmentValues, loadModesTypes } from '@common/commonLists';
import { flagOptionList } from '@components/FlagPopup/constants';
import {
  accessorialArrayValue,
  accessorialList,
  AccessorialsCellRenderer,
} from '@components/Grid/components/AccessorialsRenderer';
import FlagTypeCell, {
  AllFlagsTypeCell,
} from '@components/Grid/components/FlagTypeCell';
import LoadBoardStatusCell from '@components/Grid/components/LoadBoardStatusCell';
import RateConCellRenderer from '@components/Grid/components/RateConCellRenderer';
import RenderPostings from '@components/Grid/components/RenderPostings';
import { TrackingCellRenderer } from '@components/Grid/components/TrackingCell';
import { ILoadBoardViewModel, ValuesOf } from '@store/loadboard/types';
import memoize from 'lodash/memoize';

import { RulesOrPicklistFilter } from './RulesOrPicklistFilter';
import {
  currencyCellFormatter,
  dateComparator,
  dateFilterParamsFor,
  dateFormatter,
  fullDateFormatterYear,
  fullDateFormatterYearNoTime,
  hourAndMinuteMatcherFromText,
  lastCheckDateFormatter,
  phoneNumberCellFormatter,
  sortKeyComparatorFactory,
  weightValueCellFormatter,
} from './utils';

export { dateFormatter };

const currencyCellFormatterMemoized = memoize(currencyCellFormatter);

const suppressSelectNavKeys = (params: SuppressKeyboardEventParams) => {
  const k = params.event.key;
  return (
    k === 'ArrowDown' ||
    k === 'ArrowUp' ||
    k === 'Enter' ||
    k === ' ' ||
    k === 'Escape' ||
    k === 'Home' ||
    k === 'End'
  );
};

export const MASS_SHARE_CAPACITY_SELECT_FIELD = 'massShareCapacitySelect';

export const nonClickableColumns = [
  'status',
  'postingId',
  'confirmationSigned',
  MASS_SHARE_CAPACITY_SELECT_FIELD,
];

export const loadboardStatusArray = [
  'Assigned',
  'Unassigned',
  'Dispatched',
  'Loaded',
  'At Shipper',
  'At Receiver',
  'Out On Delivery',
  'In Transit',
  'Empty',
  'Can Get',
];

export const namedFilterParams = {
  filters: [
    {
      filter: 'agSetColumnFilter',
    },
    {
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 10,
        numAlwaysVisibleConditions: 1,
      },
    },
  ],
};

const mainLeadingColumns: ColDef[] = [
  {
    field: 'customerName',
    headerName: 'Customer',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: RulesOrPicklistFilter,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'customerTier',
    headerName: 'Customer Tier',
    initialWidth: 80,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['', 'Tier 1', 'Tier 2', 'Tier 3'],
    },
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'carrierName',
    headerName: 'Carrier',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: RulesOrPicklistFilter,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'equipment',
    headerName: 'Equipment',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: equipmentValues,
    },
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'stopsRatio',
    headerName: 'Stops',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'origin',
    headerName: 'Origin',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'originCity',
    headerName: 'Origin City',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    cellClass: 'underlined-cell',
    wrapText: true,
  },
  {
    field: 'originState',
    headerName: 'Origin State',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'originCountry',
    headerName: 'Origin Country',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'destination',
    headerName: 'Destination',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'destinationCity',
    headerName: 'Destination City',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'destinationState',
    headerName: 'Destination State',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'destinationCountry',
    headerName: 'Destination Country',
    initialWidth: 150,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'underlined-cell',
  },
  {
    field: 'pickDate',
    headerName: 'Pickup',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('pickDate'),
    cellRenderer: 'underlinedCellRenderer',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'pickUpTime',
    headerName: 'Pickup Time',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'dropDate',
    headerName: 'Drop Off',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('dropDate'),
    cellRenderer: 'underlinedCellRenderer',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'dropOffTime',
    headerName: 'Dropoff Time',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'status',
    initialWidth: 180,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: loadboardStatusArray,
    },
    cellRenderer: LoadBoardStatusCell,
    suppressKeyboardEvent: suppressSelectNavKeys,
  },
  {
    field: 'owner',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: RulesOrPicklistFilter,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'assignedTo',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: RulesOrPicklistFilter,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'capacityOpsRep',
    initialWidth: 200,
    resizable: true,
    hide: true,
    filter: RulesOrPicklistFilter,
    wrapText: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'flagType',
    headerName: 'Flag',
    initialWidth: 110,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    cellRenderer: FlagTypeCell,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'orderFlags',
    headerName: 'Active Flags',
    initialWidth: 110,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    cellRenderer: AllFlagsTypeCell,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
    // Return every flag type on the load as an array so the Set Filter matches a
    // load when ANY of its flags is selected. Returning the array lets AG Grid do a
    // proper intersection match, so loads with multiple flags still appear when
    // filtering by one of them (previously the flags were joined into a single
    // string, which only matched loads that had exactly one selected flag).
    filterValueGetter: (params) => {
      const orderFlags =
        (params.node?.data as ILoadBoardViewModel | undefined)?.orderFlags ??
        [];

      return orderFlags.map((f) => f.flagType);
    },
    filterParams: {
      filter: 'agSetColumnFilter',
      values: Object.keys(flagOptionList),
    },
  },
  {
    field: 'mode',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: loadModesTypes,
    },
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'office',
    initialWidth: 200,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'confirmationSigned',
    initialWidth: 150,
    filter: false,
    headerName: 'Rate Con',
    cellRenderer: RateConCellRenderer,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'hasTracking',
    minWidth: 80,
    width: 80,
    resizable: true,
    hide: false,
    headerName: 'Tracking',
    cellRenderer: TrackingCellRenderer,
    wrapText: true,
  },
  {
    field: 'lastCheckCallDate',
    headerName: 'Last Check Call Date',
    initialWidth: 200,
    resizable: true,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agDateColumnFilter',
          filterParams: dateFilterParamsFor('lastCheckCallDate'),
        },
        {
          filter: 'agTextColumnFilter',
          filterParams: {
            maxNumConditions: 1,
            filterPlaceholder: 'Enter HH:mm time',
            trimInput: true,
            textMatcher: hourAndMinuteMatcherFromText,
          },
        },
      ],
    },
    valueFormatter: memoize(lastCheckDateFormatter),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'lastCheckCallComment',
    headerName: 'Last Check Call Comment',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'nextCheckCallDateUtc',
    headerName: 'Next Check Call Date',
    initialWidth: 200,
    resizable: true,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agDateColumnFilter',
          filterParams: dateFilterParamsFor('nextCheckCallDateUtc'),
        },
        {
          filter: 'agTextColumnFilter',
          filterParams: {
            maxNumConditions: 1,
            filterPlaceholder: 'Enter HH:mm time',
            trimInput: true,
            textMatcher: hourAndMinuteMatcherFromText,
          },
        },
      ],
    },
    valueFormatter: memoize(fullDateFormatterYear),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'pendingBids',
    initialWidth: 100,
    resizable: true,
    hide: false,
    filter: 'agNumberColumnFilter',
    cellRenderer: 'underlinedCellRenderer',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'totalBids',
    initialWidth: 100,
    resizable: true,
    hide: false,
    filter: 'agNumberColumnFilter',
    cellRenderer: 'underlinedCellRenderer',
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'loadCreatedDateUtc',
    headerName: 'Load Created Date',
    initialWidth: 200,
    resizable: true,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('loadCreatedDateUtc'),
    valueFormatter: memoize(fullDateFormatterYearNoTime),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'maxPrice',
    headerName: 'Max Rate',
    initialWidth: 125,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatterMemoized,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'minPrice',
    headerName: 'Starting Rate',
    initialWidth: 125,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatterMemoized,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'cargoValue',
    headerName: 'Cargo Value',
    initialWidth: 200,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatterMemoized,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'driverPhone',
    headerName: 'Driver Contact',
    initialWidth: 200,
    resizable: true,
    filter: 'agTextColumnFilter',
    valueFormatter: memoize(phoneNumberCellFormatter),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'weightKg',
    headerName: 'Weight (kg)',
    initialWidth: 150,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: memoize(weightValueCellFormatter),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'weightLbs',
    headerName: 'Weight (lbs)',
    initialWidth: 150,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: memoize(weightValueCellFormatter),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'carrierPay',
    headerName: 'Carrier Pay',
    initialWidth: 200,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatterMemoized,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'customerPay',
    headerName: 'Customer Pay',
    initialWidth: 200,
    resizable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatterMemoized,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'unfilteredLastCheckCallDate',
    headerName: 'Last Comment Date',
    initialWidth: 200,
    minWidth: 200,
    maxWidth: 250,
    resizable: true,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agDateColumnFilter',
          filterParams: dateFilterParamsFor('unfilteredLastCheckCallDate'),
        },
        {
          filter: 'agTextColumnFilter',
          filterParams: {
            maxNumConditions: 1,
            filterPlaceholder: 'Enter HH:mm time',
            trimInput: true,
            textMatcher: hourAndMinuteMatcherFromText,
          },
        },
      ],
    },
    valueFormatter: memoize(fullDateFormatterYear),
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'unfilteredLastCheckCallComment',
    headerName: 'All Comments',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  // OperationsRep - filter similar to coveredBy
  {
    field: 'operationsRep',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    filter: RulesOrPicklistFilter,
    hide: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'originTimeMilitary',
    headerName: 'Pick Up 24hr',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    comparator: sortKeyComparatorFactory<ILoadBoardViewModel>(
      'pickUpTimeMilitarySortKey',
    ),
    hide: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'originTimeComments',
    headerName: 'Origin Comments',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    hide: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'destinationTimeMilitary',
    headerName: 'Dropoff 24hr',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    comparator: sortKeyComparatorFactory<ILoadBoardViewModel>(
      'dropOffTimeMilitarySortKey',
    ),
    hide: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'destinationTimeComments',
    headerName: 'Dropoff Comments',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    hide: true,
    cellClass: 'my-custom-cell-class',
  },
  {
    field: 'truckNumber',
    headerName: 'Truck No.',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'trailerNumber',
    headerName: 'Trailer No.',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'rateConStatus',
    headerName: 'HWY Secure Rate Con Status',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    headerName: 'Accessorials',
    field: 'accessorials',
    initialWidth: 200,
    resizable: true,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
    cellRenderer: AccessorialsCellRenderer,
    filterValueGetter: (params) => {
      const accessorials = accessorialArrayValue(params);
      const values =
        (params.api.getFilterModel() as { accessorials?: { values: string[] } })
          .accessorials?.values ?? [];

      const allValues: string[] = [];

      values.forEach((v) => {
        if (accessorials.includes(v)) {
          allValues.push(v);
        }
      });
      return allValues.join(' ');
    },
    filterParams: {
      filter: 'agSetColumnFilter',
      values: accessorialList,
    },
  },
  {
    field: 'aiTrackingStatusLocalized',
    minWidth: 80,
    initialWidth: 80,
    resizable: true,
    hide: false,
    headerName: 'AI Tracking',
    wrapText: true,
  },
];

// There cannot be duplicate column names here. If you move or add a column make sure
// it's not in here twice. It will cause problems with AG Grid.
const defaultColumns = [
  {
    field: 'postingId',
    headerName: 'Post',
    minWidth: 80,
    width: 80,
    resizable: true,
    hide: false,
    cellRenderer: RenderPostings,
    filter: false,
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },
  {
    field: 'loadNumber',
    headerName: 'LoadNum',
    initialWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    // This is an example of how we can restrict allowed pivot functions for certain columns.
    allowedAggFuncs: ['count'],
    wrapText: true,
    cellClass: 'my-custom-cell-class', // Add this line
  },

  ...mainLeadingColumns,
];
const textColumns: [string, number, ColDef['filter']?, string?][] = [
  ['referenceNumber', 200],
  ['claim', 100],
  ['product', 150],
  ['miles', 100, 'agNumberColumnFilter'],
  ['sharedCapacity', 150],
  ['trackingNumber', 150],
  ['proNumber', 150],
  ['stopPickup', 150],
  ['stopDropoff', 150],
  ['my', 100, undefined, 'My Loads'],
  ['myCustomers', 100],
  ['accountManager', 200, RulesOrPicklistFilter],
  ['salesManager', 200, RulesOrPicklistFilter],
];

const namedFields: [string, number][] = [
  // ['assignedTo', 200],
  ['coveredBy', 200],
  // ['accountManager', 200],
  // ['customerName', 200],
  // ['carrierName', 200],
  // ['salesManager', 200],
  // ['equipment', 120],
];

export const calculateNewHeight = (
  comment: string | undefined | null,
  colWidth: number,
): number | undefined => {
  const height = 12;
  const lineHeight = height * 1.332;

  if (typeof comment !== 'string') {
    return undefined;
  }

  const charsPerLine = Math.ceil(colWidth / (height / 2));
  const newHeight = (comment.length / charsPerLine) * lineHeight + 12;

  if (newHeight < 42) {
    return undefined;
  }

  return newHeight;
};

export const autoHeightColumns = [
  'lastCheckCallComment',
  'referenceNumber',
  'originTimeComments',
  'destinationTimeComments',
  'originTimeMilitary',
  'destinationTimeMilitary',
];

export const columnDefs: ColDef[] = [
  ...defaultColumns,
  ...textColumns.map(([field, initialWidth, filter, headerName]): ColDef => {
    return {
      field,
      headerName,
      initialWidth,
      resizable: true,
      filter: filter ?? 'agTextColumnFilter',
      suppressNavigable: true,
      wrapText: true,

      cellClass: 'my-custom-cell-class', // Add this line
    };
  }),
  ...namedFields.map(([field, initialWidth]) => {
    return {
      field,
      initialWidth,
      resizable: true,
      filter: RulesOrPicklistFilter,
    };
  }),
];

export const columnDefsMap = columnDefs.reduce(
  (prev, curr) => {
    if (curr.field === undefined) {
      return prev;
    }

    prev[curr.field] = curr;
    return prev;
  },
  {} as Record<string, ColDef>,
);

type StatusTypesT = ValuesOf<typeof loadboardStatusArray>;
export const nextCheckCallByStatus: Record<
  StatusTypesT | 'None',
  StatusTypesT | '-'
> = {
  Assigned: 'Dispatched',
  Unassigned: 'Assigned',
  Dispatched: 'At Shipper',
  'At Shipper': 'Loaded',
  Loaded: 'In Transit',
  'In Transit': 'Out On Delivery',
  'At Receiver': 'Empty',
  'Out On Delivery': 'At Receiver',
  Empty: '-',
  'Can Get': 'Assigned',
  None: '-',
  Void: '-',
};

export const TransitWarningColumnDefs: ColDef[] = [
  {
    field: 'loadNumber',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'customerName',
    minWidth: 180,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'carrierName',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'origin',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('origin'),
  },
  {
    field: 'destination',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('destination'),
  },
  {
    field: 'pickDate',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('pickDate'),
    valueFormatter: dateFormatter,
    comparator: dateComparator,
  },
  {
    field: 'dropDate',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParamsFor('dropDate'),
    valueFormatter: dateFormatter,
    comparator: dateComparator,
  },
  {
    field: 'owner',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'status',
    minWidth: 120,
    resizable: true,
    hide: false,
    editable: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: loadboardStatusArray,
    } as IRichCellEditorParams,
  },
  {
    field: 'flagType',
    minWidth: 120,
    resizable: true,
    hide: false,
    filter: 'agTextColumnFilter',
    cellRenderer: FlagTypeCell,
  },
];
